/**
 * Changes from either feed as rows of a changes card, and dismissing them
 * through each one's own feed. Shared by the Points, Offers and Refresh cards.
 */
import type { ReactNode } from "react";
import { Link } from "react-router";

import {
  dismissOfferChanges,
  undismissOfferChanges,
  type OfferChange,
} from "../lib/offerChanges";
import {
  changeDay,
  dismissChanges,
  undismissChanges,
  type PointsChange,
  type RunPart,
} from "../lib/pointsChanges";

type Glyph = "up" | "down" | "credit" | "status" | "offer" | "held";

export const ICON: Record<Glyph, { glyph: string; tone: string }> = {
  up: { glyph: "▲", tone: "bg-green-500/15 text-green-400" },
  down: { glyph: "▼", tone: "bg-red-500/15 text-red-400" },
  credit: { glyph: "$", tone: "bg-amber-500/15 text-amber-400" },
  status: { glyph: "★", tone: "bg-blue-400/15 text-blue-300" },
  offer: { glyph: "%", tone: "bg-orange-500/15 text-orange-400" },
  held: { glyph: "!", tone: "bg-amber-500/25 text-amber-300" },
};

/** One row of a changes card, from either feed. */
export interface ChangeRow {
  /** The thing, unique in its card. */
  key: string;
  /** What dismissing it sends, to the feed named by `area`. */
  keys: string[];
  area: "points" | "offers";
  glyph: Glyph;
  title: string;
  sub: string | null;
  body: ReactNode;
  day: string | null;
  /** A held reading's event: the row is settled by Approve or Reject, never
   *  dismissed, and Dismiss all leaves it. */
  review?: number;
}

export interface ChangeGroup {
  label?: string;
  link?: { to: string; text: string };
  rows: ChangeRow[];
}

function keysOf(rows: ChangeRow[], area: ChangeRow["area"]): string[] {
  return rows.filter((r) => r.area === area).flatMap((r) => r.keys);
}

/** Dismiss rows through each one's own feed. Dismissed everywhere it shows. */
export async function dismissRows(rows: ChangeRow[]): Promise<void> {
  const points = keysOf(rows, "points");
  const offers = keysOf(rows, "offers");
  await Promise.all([
    ...(points.length ? [dismissChanges(points)] : []),
    ...(offers.length ? [dismissOfferChanges(offers)] : []),
  ]);
}

export async function restoreRows(rows: ChangeRow[]): Promise<void> {
  const points = keysOf(rows, "points");
  const offers = keysOf(rows, "offers");
  await Promise.all([
    ...(points.length ? [undismissChanges(points)] : []),
    ...(offers.length ? [undismissOfferChanges(offers)] : []),
  ]);
}

/**
 * A Points change as a row. `net` is everything since it was last dismissed
 * (Points); `run` is the part the change's newest run made (Refresh), with the
 * net beside it when earlier undismissed moves make the two differ.
 */
export function pointsRow(c: PointsChange, mode: "net" | "run", today?: string): ChangeRow {
  const part: Omit<RunPart, "run_id"> = mode === "run" && c.run ? c.run : c;
  const sub = [c.account, c.credit].filter(Boolean).join(" · ") || null;
  const glyph: Glyph = part.kind === "new_credit" || part.kind === "credit" ? "credit" : part.kind;
  const netDiffers = mode === "run" && c.run !== null && c.run.from !== c.from;
  return {
    key: c.key,
    keys: c.keys,
    area: "points",
    glyph,
    title: c.program,
    sub,
    day: today ? changeDay(c.on, today) : null,
    review: c.event_id,
    body: (
      <>
        {changeText(part)}
        {mode === "net" && c.moves > 1 && (
          <span className="ml-1.5 rounded-full border border-blue-400/30 px-1.5 text-[11px] text-blue-300">
            {c.moves} changes
          </span>
        )}
        {netDiffers && (
          <span className="block text-xs text-neutral-500">
            {c.kind === "up" || c.kind === "down"
              ? <>net <span className="tabular-nums">{c.delta}</span> since you last looked</>
              : <>was {c.from ?? "new"} when you last looked</>}
          </span>
        )}
        {c.on_credits_page && (
          <>
            {" · "}
            <Link to="/" className="text-blue-300">
              on Credits
            </Link>
          </>
        )}
      </>
    ),
  };
}

/** A new offer worth adding as a row, dated when `today` is given. */
export function offerRow(c: OfferChange, today?: string): ChangeRow {
  return {
    key: c.key,
    keys: [c.key],
    area: "offers",
    glyph: "offer",
    title: c.merchant,
    // Already added on some cards: say which, so the row is not read as new
    // on all of them.
    sub: c.added_on?.length
      ? `Added on ${c.added_on.join(", ")} · not yet on ${c.cards.join(", ")}`
      : c.cards.join(", ") || c.issuer,
    day: today ? changeDay(c.on, today) : null,
    body: <>New, worth adding{c.terms && <>: {c.terms}</>}</>,
  };
}

function changeText(c: Omit<RunPart, "run_id">): ReactNode {
  if (c.kind === "new_credit") {
    return <>New credit, <span className="tabular-nums">{c.to}</span> available</>;
  }
  const arrow = <span className="px-1 text-neutral-500">→</span>;
  if (c.kind === "status") {
    return <>Status {c.from}{arrow}<b>{c.to}</b></>;
  }
  return (
    <>
      {c.from && <><span className="tabular-nums">{c.from}</span>{arrow}</>}
      <b className="tabular-nums">{c.to}</b>
      {c.delta && (
        <span className={`ml-1.5 text-xs tabular-nums ${c.delta.startsWith("+") ? "text-green-400" : "text-red-400"}`}>
          {c.delta}
        </span>
      )}
      {c.used_up && <span className="ml-1.5 text-neutral-500">used up</span>}
      {c.kind === "held" && (
        <span className="block text-xs text-amber-300">
          Held for review, not counted: check it on the site, then approve or reject
        </span>
      )}
    </>
  );
}

